import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, LinearProgress, Stack, Chip,
  CircularProgress, Alert, useTheme,
} from '@mui/material';
import { WorkspacePremiumRounded, CheckCircleRounded } from '@mui/icons-material';
import SubscriptionService from '../../services/subscription.service';
import { isB2bTc } from '../../utils/roleUtils';
import { getBrandColor, getBrandContrastText, getBrandInk } from '../../utils/colorUtils';

const SubscriptionUsage = () => {
  const theme = useTheme();
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  let currentUser = null;
  try { currentUser = JSON.parse(localStorage.getItem('currentUser')); } catch (e) { currentUser = null; }
  const isB2b = isB2bTc(currentUser);

  useEffect(() => {
    SubscriptionService.getCurrent()
      .then((c) => setCurrent(c))
      .catch(() => setError('Failed to load usage'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}><CircularProgress /></Box>;
  }

  const usage = current?.usage || [];
  const brandColor = getBrandColor(theme);
  const brandContrastText = getBrandContrastText(theme);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 0.5 }}>Usage &amp; limits</Typography>
      <Typography variant="caption" color="text.secondary">Track how much of your plan you've used this period.</Typography>

      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

      {/* Current plan + status */}
      <Card sx={{ mt: 2, mb: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider', borderLeft: `4px solid ${getBrandInk(theme)}` }}>
        <CardContent sx={{ p: 3 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ width: 52, height: 52, borderRadius: 2, bgcolor: brandColor, color: brandContrastText, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <WorkspacePremiumRounded />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Current plan</Typography>
                <Typography variant="h5" sx={{ lineHeight: 1.2 }}>{current?.plan_name || 'Free'}</Typography>
                {current?.period_end && (
                  <Typography variant="caption" color="text.secondary">Renews / expires on {current.period_end}</Typography>
                )}
              </Box>
            </Stack>
            {current?.status && (
              <Chip label={SubscriptionService.getStatusLabel(current.status)} color={SubscriptionService.getStatusColor(current.status)} sx={{ fontWeight: 600 }} />
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Usage this period */}
      <Card>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Usage this period</Typography>
          {usage.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No usage data.</Typography>
          ) : (
            <Grid container spacing={2.5}>
              {usage.map((u) => {
                const pct = u.unlimited ? 0 : SubscriptionService.getUsagePercentage(u.used, u.limit);
                const remaining = u.unlimited ? null : Math.max(0, (u.limit || 0) - (u.used || 0));
                return (
                  <Grid item xs={12} sm={6} key={u.feature_key}>
                    <Box sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', height: '100%' }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="body2" fontWeight={600}>{SubscriptionService.getFeatureLabel(u.feature_key, isB2b)}</Typography>
                        {u.unlimited
                          ? <Chip size="small" icon={<CheckCircleRounded />} label="Unlimited" color="success" variant="outlined" />
                          : <Typography variant="body2" color="text.secondary">{u.used} / {u.limit}</Typography>}
                      </Stack>
                      <LinearProgress variant="determinate" value={u.unlimited ? 100 : pct}
                        color={u.unlimited ? 'success' : SubscriptionService.getUsageColor(pct)} sx={{ height: 8, borderRadius: 4 }} />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
                        {u.unlimited ? 'No limit on this plan' : `${remaining} remaining · ${pct}% used`}
                      </Typography>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default SubscriptionUsage;
